<script lang="ts">
    import type * as t from "./types";
    import type * as emailmarketing from "~/integrations/email-marketing";
    import type { SequenceNode } from "./sequence";

    import { derived, get } from "svelte/store";
    import { getAppContext } from "./quiz-store";
    import { cn, getPrizeText, getSegmentConfig } from "./utils";
    import { saveContactToGoogleSheet } from "./integrations/google-sheets";
    import { subscribeCustomer } from "./integrations/email-marketing";
    import { getShopifyDiscountCodeUrl } from "~/utils";
    import StepHeader from "./components/StepHeader.svelte";
    import EmailMarketingForm from "./components/EmailMarketingForm.svelte";
    import ActionButton from "./components/ActionButton.svelte";
    import PaddingX from "./components/PaddingX.svelte";
    import type { Action } from "svelte/action";

    export let visible: boolean;
    export let segmentNode: SequenceNode<t.Segment>;
    export let step: t.SegmentResultStep;

    const STORE_HANDLE = window.Shopify.shop;
    const ctx = getAppContext();
    const {
        state,
        actions: { goToNextStep, calculateResult },
        telemetry,
    } = ctx;
    const quizConfig = $state.config;
    const emailMktConfig = $state.emailMarketingConfig;
    const config = derived(state, ($) => {
        const segmentConfig = getSegmentConfig($.config, segmentNode);
        if (segmentConfig == null) {
            throw new Error("Unexpected null config.");
        }
        return segmentConfig;
    });
    const nextConfig = getSegmentConfig(quizConfig, segmentNode.next);

    if (config == null) {
        throw new Error(
            `[shpln-quiz] Config of Segment at index ${segmentNode.idx} is null.`,
        );
    }

    const prize = derived(config, ($) => $.prize);
    let prizeText: string;
    $: prizeText = getPrizeText($prize);

    let networkStatus: "idle" | "loading" = "idle";
    let formErrors: Record<string, string>;
    let formStatus: t.SegmentResultStep["state"]["formStatus"] =
        nextConfig == null ? "visible" : "hidden";
    let isPhoneInputValid = true;
    let phoneInputError: string | undefined;
    let formInputType: "phone" | "email" = step.defaultFormInputType;
    let appWidth: number | undefined;

    $: revalidatePhoneInput(isPhoneInputValid);

    function handleContinue() {
        telemetry.logEvent("continue_playing", {
            level_number: segmentNode.idx + 1,
            next_level_number: segmentNode.next
                ? segmentNode.next.idx + 1
                : undefined,
        });
        goToNextStep();
    }

    function handleClaimPrize() {
        formStatus = "visible";
    }

    async function captureContact(
        formData: FormData,
        properties?: Record<string, any>,
    ) {
        const [shop] = STORE_HANDLE.split(".");
        const email = formData.get("email") as string | undefined;
        const phone = formData.get("phone") as string | undefined;

        const googleSheetPromise = saveContactToGoogleSheet(formData);
        let emailMktPromise: Promise<void | undefined>;

        const subscriptions: emailmarketing.Subscription[] = [];
        for (const provider of emailMktConfig.enabledProviders) {
            subscriptions.push({
                provider,
                email,
                phone,
                properties,
            });
        }
        if (subscriptions.length > 0) {
            emailMktPromise = subscribeCustomer(subscriptions, shop);
        } else {
            emailMktPromise = Promise.resolve();
        }

        const results = await Promise.allSettled([
            googleSheetPromise,
            emailMktPromise,
        ]);
        let rejected = [];
        for (const result of results) {
            if (result.status !== "rejected") {
                continue;
            }
            rejected.push(result.reason);
        }

        if (rejected.length > 0) {
            const msg = rejected
                .map((reason) => reason?.message ?? reason)
                .join("\n");
            console.error("[shpln-quiz] Unable to save contact", msg);
        }
    }

    async function handleSubmitForm(e: SubmitEvent) {
        if (Object.keys(formErrors).length > 0) {
            return;
        }

        networkStatus = "loading";

        if (segmentNode.next != null) {
            telemetry.logEvent("abandon_game", {
                level_number: segmentNode.idx + 1,
                total_levels: quizConfig.segments.length,
            });
        }

        let discountPrizeInfo = {};
        if ($prize.type === "discount") {
            discountPrizeInfo = {
                prize_discount_type: $prize.discount_type,
                prize_discount_value: $prize.discount_value,
            };
        }

        let customPrizeInfo = {};
        if ($prize.type === "custom") {
            customPrizeInfo = {
                prize_custom_value: $prize.custom_value,
            };
        }

        telemetry.logEvent("claim_prize", {
            level_number: segmentNode.idx + 1,
            prize_type: $prize.type,
            ...discountPrizeInfo,
            ...customPrizeInfo,
        });

        const minigameResult = get(state).minigameResult;
        let result: t.QuizResult | undefined;
        if (minigameResult != null) {
            result = minigameResult;
        } else {
            result = calculateResult(segmentNode);
        }

        let discountUrl: string | undefined;
        if (result?.discountCode != null) {
            discountUrl = getShopifyDiscountCodeUrl(
                result.discountCode,
                result.redirectUrl,
            );
        }

        const formData = new FormData(e.target as HTMLFormElement);
        const properties = {
            playably__redirect_url: result?.redirectUrl,
            playably__discount_url: discountUrl,
        };
        await captureContact(formData, properties);

        /*
         * This should not happen, but we add a guard to prevent crashes.
         * */
        if (result == null) {
            return;
        }

        let href: string;
        if ($prize.type === "custom") {
            href = result.redirectUrl;
        } else {
            if (discountUrl == null) {
                console.error(
                    "[shpln-quiz] Discount URL is unexpectedly null. Will fallback to redirect URL.",
                );
            }
            href = discountUrl ?? result.redirectUrl;
        }

        window.location.href = href;
    }

    function revalidatePhoneInput(isValid: boolean) {
        if (phoneInputError == null) {
            return;
        }

        if (isValid) {
            phoneInputError = undefined;
        }
    }

    $: formStatus &&
        state.update(($) => {
            if ($.step.value.type !== "segment_result") return $;
            $.step.value.state.formStatus = formStatus;
            return $;
        });

    const setupAppWidth: Action = (node) => {
        const appContainer = ctx.host.shadowRoot?.querySelector(".app");
        appWidth = appContainer?.clientWidth;
    };
</script>

{#if visible}
    <StepHeader
        title={step.title}
        subtitle={step.subtitle?.text}
        class="w-full"
    />
    <PaddingX
        class="flex w-full flex-1 flex-col items-center justify-center gap-6"
    >
        {#if step.image != null}
            <div
                class="img-container relative aspect-square flex-1"
                style={cn(appWidth && `--app-width: ${appWidth}px`)}
                use:setupAppWidth
            >
                <img
                    src={step.image.src}
                    alt={step.image.alt}
                    width={step.image.width}
                    height={step.image.height}
                    class="absolute inset-0 object-contain"
                    loading="lazy"
                    aria-hidden="true"
                />
            </div>
        {/if}

        <div
            class="flex w-full flex-col justify-center"
            class:pointer-events-none={networkStatus === "loading"}
        >
            <div
                class={cn(
                    "flex w-full flex-col items-stretch gap-8 transition-[height]",
                    formStatus === "visible" && "h-full",
                )}
            >
                <div
                    class="transition-height flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2"
                >
                    {#if formStatus === "visible"}
                        <EmailMarketingForm
                            bind:errors={formErrors}
                            onSubmit={handleSubmitForm}
                            enabledInputTypes={step.enabledInputTypes}
                            emailInputPlaceholder={step.emailInputPlaceholder}
                            ctaSubtitle={step.claimPrizeCtaSubtitle}
                            {formInputType}
                            {networkStatus}
                        >
                            <span slot="ctaText">
                                Claim Your <span class="font-bold">
                                    {prizeText}
                                </span>
                            </span>
                        </EmailMarketingForm>
                    {:else}
                        {#if nextConfig != null}
                            <ActionButton
                                on:click={handleContinue}
                                type="button"
                            >
                                <span slot="title">
                                    {step.continueButton.text}
                                </span>
                            </ActionButton>
                        {/if}

                        <ActionButton
                            on:click={handleClaimPrize}
                            type="button"
                            buttonStyle={nextConfig == null
                                ? "gradient-background"
                                : "gradient-outline"}
                        >
                            <span slot="title">
                                Take the <span class="font-bold"
                                    >{prizeText}</span
                                >
                            </span>
                        </ActionButton>
                    {/if}
                </div>
            </div>
            {#if formStatus === "visible" && quizConfig.privacyPolicyUrl != null}
                <div
                    class="mt-4 text-center text-xs leading-none text-gray-300"
                >
                    We use your data to send updates and offers. See our <a
                        class="font-bold underline"
                        href={quizConfig.privacyPolicyUrl}>Privacy Policy</a
                    >.
                </div>
            {/if}
        </div>
    </PaddingX>
{/if}

<style lang="scss">
    button.claim {
        color: var(--primary-color);
        border-style: solid;
        border-width: 2px;
        border-color: var(--primary-color);
        background-color: whitesmoke;
    }

    button.continue {
        color: var(--text-on-primary-color);
        background-color: var(--primary-color);
    }

    form input,
    .phone-input-wrapper {
        background-color: whitesmoke;
    }

    .phone-input-wrapper {
        :global(.basic-tel-input),
        :global(.country-select) {
            height: 100%;
            padding-left: 8px;
            background-color: whitesmoke;
        }

        :global(.country-select) {
            padding: 0;
        }

        :global(.basic-tel-input:focus),
        :global(.country-select:focus) {
            outline: none;
        }
    }

    .phone-input-wrapper.invalid {
        border-color: var(--error-color);
    }

    .img-container {
        max-height: calc(var(--app-width) * 0.85);
    }
</style>
